<template>
  <v-form ref="formStepGenericPricing" v-model="formStepGenericPricingValid" @submit.prevent="submit">
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-xl mt-4 d-flex-column" :border="false" :elevation="0">
          <v-row>
            <v-col cols="12">
              <h1>{{ $t('FILE_STEP_PRICING.TITLE') }}</h1>
            </v-col>
            <v-col cols="12" v-if="isInGuarantee()">
              {{ $t('FILE_STEP_PRICING.UNDER_GUARANTEE') }}
            </v-col>
            <v-col cols="12" v-if="!isInGuarantee()">
              <v-radio-group v-model="packageType" :rules="requiredRules" hide-details>
                <v-row class="d-flex justify-center">
                  <v-col class="d-flex" cols="6">
                    <v-card
                      :class="{ active: packageType === 'ESTIMATE' }"
                      :elevation="0"
                      class="package-info rounded-xl flex-fill"
                    >
                      <v-radio value="ESTIMATE" />
                      <p class="card-title">{{ $t('FILE_STEP_PRICING.PACKAGE_INFO.ESTIMATE') }}</p>
                      <p class="card-sub-title">-</p>
                      <div class="w-100 d-flex justify-space-between align-left">
                        <p class="font-weight-bold">
                          {{ $t('FILE_STEP_PRICING.PACKAGE_INFO.REPAIR_TIME') }}
                        </p>
                        <p>{{ 28 + ' ' + $t('FILE_STEP_PRICING.PACKAGE_INFO.DAYS') }}</p>
                      </div>

                      <!--Diag message-->
                      <div class="w-100 d-flex justify-space-between align-left">
                        <p class="font-weight-bold">
                          {{ $t('FILE_STEP_PRICING.PACKAGE_INFO.REPAIR_AMOUNT') }}
                        </p>
                        <p>{{ $t('FILE_STEP_PRICING.PACKAGE_INFO.WITH_DIAGNOSTIC') }}</p>
                      </div>

                      <!--Transport cost for estimate-->
                      <div
                        v-if="estimate_transporter_cost !== 0"
                        class="w-100 d-flex justify-space-between align-left"
                      >
                        <p class="font-weight-bold">
                          {{ $t('FILE_STEP_PRICING.PACKAGE_INFO.ESTIMATE_TRANSPORT_COST') }}
                        </p>
                        <p>{{ estimate_transporter_cost.toFixed(2) }} €</p>
                      </div>

                      <!--Diag cost-->
                      <div class="w-100 d-flex justify-space-between align-left">
                        <p class="font-weight-bold">
                          {{ $t('FILE_STEP_PRICING.PACKAGE_INFO.DIAGNOSTIC_COST') }}
                        </p>
                        <p>45.00 €</p>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col>
        <v-btn
          :disabled="!formStepGenericPricingValid || loading"
          :loading="loading"
          class="pl-15 pr-15"
          :class="!formStepGenericPricingValid || loading ? 'boom-button-transparent' : 'boom-button'"
          type="submit"
          variant="outlined"
        >
          {{ $t('COMMON.BUTTON.CONTINUE') }}
          <img
            alt="arrow_small-icon"
            :class="!formStepGenericPricingValid || loading ? 'arrow-black-svg' : 'arrow-white-svg'"
            src="@/assets/icons/file/arrow_small_right.svg"
          />
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { handleResponseError } from '@/components/utils/globalFunction'
import ResponseConstant from '@/constants/responseConstant'
import { auth as $store } from '@/store/AuthModule'
import RepairFileService from '@/services/RepairFileService'
import RepairFileGuaranteeConstant from '@/constants/repairFileGuaranteeConstant'
import { RepairFile } from '@/models/RepairFile'
import EstimateTransporterCostService from '@/services/repairModule/repairFile/EstimateTransporterCostService'
import Validator from '@/components/utils/validator'
import RepairFileStatusDetailConstant from '@/constants/repairFileStatusDetailConstant'

export default {
  name: 'StepGenericPricingPage',
  props: {
    currentStep: Number,
    currentRepairFile: {
      type: RepairFile,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      formStepGenericPricingValid: false,
      estimate_transporter_cost: 0,
      packageType: null,
      requiredRules: Validator.requiredRules,
      error: null
    }
  },
  async mounted() {
    await this.getEstimateTransporterCost()
  },
  methods: {
    isInGuarantee() {
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        this.formStepGenericPricingValid = true
        return true
      } else {
        return false
      }
    },
    async getEstimateTransporterCost() {
      try {
        const response = await EstimateTransporterCostService.getEstimateTransporterCost(
          this.currentRepairFile.technical_id
        )
        this.estimate_transporter_cost = response.cost
      } catch (error) {
        this.estimate_transporter_cost = 0
      }
    },
    async submit() {
      const formattedData = {
        status_detail: RepairFileStatusDetailConstant.PRICING_EVALUATED,
        is_complete_update: true,
        requestor_collaborator_id: $store.state.currentCollaborator.technical_id
      }

      if (!this.isInGuarantee()) {
        formattedData['pricing_type'] = this.packageType
      }

      try {
        const formData = new FormData()
        formData.append('data', JSON.stringify(formattedData))
        await RepairFileService.update(this.currentRepairFile.technical_id, formData)

        this.$emit('change-step', this.currentStep + 1)
      } catch (error) {
        handleResponseError(error)
      }
    },
    setError(key) {
      switch (key) {
        case 'not_found_error_key':
          this.error = { customer_code: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        default:
          this.error = ''
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  padding: 24px;
}

.v-form p {
  margin-bottom: 16px;
}

.v-form p[require-field='true']:after {
  content: ' *';
  color: var(--error-color);
}

:deep(.v-field--error) {
  border: 1px solid var(--error-color);
}

:deep(.v-field) {
  border-radius: 30px;
}

.arrow-black-svg {
  height: 100%;
  width: 50%;
  filter: invert(0%) sepia(35%) saturate(13%) hue-rotate(154deg) brightness(96%) contrast(100%);
}

.arrow-white-svg {
  height: 100%;
  width: 50%;
  filter: invert(100%) sepia(0%) saturate(1%) hue-rotate(209deg) brightness(109%) contrast(101%);
}

.boom-button-transparent:hover .arrow-svg {
  filter: invert(48%) sepia(35%) saturate(7492%) hue-rotate(64deg) brightness(105%) contrast(79%);
}

.package-info {
  text-align: center;
  border: 1px solid #559a1b;
  background-color: #efefef;
}

.package-info.active {
  color: white;
  background-color: #559a1b;
}

.package-info .v-radio {
  display: flex;
  justify-content: center;
}

.package-info p {
  margin-bottom: 5px;
}

.package-info .card-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 0;
}

.package-info .card-sub-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 20px;
}

.align-left {
  text-align: left;
}
</style>
